import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { parseParamNumber } from '../../../api/fetchers/playerConfig/parsers';
import { CoreSessionStorage } from '../../../storage/session/CoreSessionStorage';
import { getIsLanguageRTL } from '../../../utils/language';
import { fetchPlayerConfigAction } from '../../apiActions/fetchPlayerConfigAction';

import type { ChannelsType, ISettingsInitialState, VolumeChannelType } from './types';
import { StreamQuality, VolumeChannels } from './types';

const initialState: ISettingsInitialState = {
  isInFullscreen: false,
  isGameVisible: true,
  isSecondBetBoardToggle: true,
  isAnimationEnabled: false,
  stream: {
    isEnabled: true,
    isEnabledBySystem: true,
    isVideoPlaying: false,
    quality: StreamQuality.Auto,
    qualityLimit: 0, //StreamQuality.High
    streamLatency: 1000,
  },
  // TODO: Need to refactor this channel disabling
  volume: {
    restored: false,
    master: {
      value: 0,
      temporaryValue: null,
      previousValue: CoreSessionStorage.getLastPositiveMasterVolume() ?? 1,
    },
    channels: {},
    uiChannel: null,
  },
  language: {},
  showOtherMessages: true,
  settingsTabToHide: [],
};

export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setIsInFullscreen: (state, action: PayloadAction<boolean>) => {
      state.isInFullscreen = action.payload;
    },
    /* Stream */
    // Listened by settingSyncMiddleware
    setStreamEnabled: (state, action: PayloadAction<boolean>) => {
      state.stream.isEnabled = action.payload;
    },
    // Flag used for managing stream on inactivity
    setStreamEnabledBySystem: (state, action: PayloadAction<boolean>) => {
      state.stream.isEnabledBySystem = action.payload;
    },
    setIsVideoPlaying: (state, action: PayloadAction<boolean>) => {
      state.stream.isVideoPlaying = action.payload;
    },
    setStreamQualityLimit: (state, action: PayloadAction<number>) => {
      state.stream.qualityLimit = action.payload;
    },
    setStreamQuality: (state, action: PayloadAction<StreamQuality>) => {
      state.stream.quality = action.payload;
    },
    setStreamLatency: (state, action: PayloadAction<number>) => {
      state.stream.streamLatency = action.payload;
    },
    isAnimationToggle: (state) => {
      state.isAnimationEnabled = !state.isAnimationEnabled;
    },
    isSecondBetBoardToggle: (state) => {
      state.isSecondBetBoardToggle = !state.isSecondBetBoardToggle;
    },

    /* Chat */
    setShowOtherMessages: (state, action: PayloadAction<boolean>) => {
      state.showOtherMessages = action.payload;
    },

    /* Volume */
    setGameVolumeChannels: (state, action: PayloadAction<string[]>) => {
      const newChannels = action.payload.reduce(
        (acc, channel) => {
          if (state.volume.channels[channel]) return acc;
          acc[channel] = {
            value: 0,
            temporaryValue: null,
            previousValue: 1,
          };
          return acc;
        },
        {} as { [key: string]: VolumeChannelType }
      );
      state.volume.channels = {
        ...state.volume.channels,
        ...newChannels,
      };
    },

    setMasterVolumeOnly: (state, action: PayloadAction<number>) => {
      // Update only master value without syncing studio/game channels
      const newVolume = action.payload;
      state.volume.restored = true;
      state.volume.master.value = newVolume;
      updateMasterVolume(state, newVolume);
    },

    toggleMasterVolume: (state) => {
      const newVolume = state.volume.master.value ? 0 : state.volume.master.previousValue;
      setMasterVolumeFunc(state, newVolume);
    },

    mute: (state) => {
      setMasterVolumeFunc(state, 0);
    },

    unmute: (state) => {
      setMasterVolumeFunc(state, state.volume.master.previousValue);
    },

    // Listened by settingSyncMiddleware
    setMasterVolume: (state, action: PayloadAction<number>) => {
      setMasterVolumeFunc(state, action.payload);
    },
    setMasterVolumeTemporary: (state, action: PayloadAction<number | null>) => {
      state.volume.master.temporaryValue = action.payload;
    },

    setAllVolumes: (state, action: PayloadAction<number>) => {
      state.volume.restored = true;
      Object.keys(state.volume.channels).forEach((key) => {
        if (!state.volume.channels[key]) return;
        state.volume.channels[key].value = action.payload;
      });
    },

    // Listened by settingSyncMiddleware
    setVolume: (
      state,
      action: PayloadAction<{ channel: keyof (typeof initialState)['volume']['channels']; value: number }>
    ) => setVolumeValue(state, action),

    // Avoid redundant savePlayerConfigGeneric API call on iOS devices in VolumeRestorer
    setVolumeIOSToggle: (
      state,
      action: PayloadAction<{ channel: keyof (typeof initialState)['volume']['channels']; value: number }>
    ) => setVolumeValue(state, action),

    setVolumeTemporary: (
      state,
      action: PayloadAction<{ channel: keyof (typeof initialState)['volume']['channels']; value: number | null }>
    ) => {
      const { channel, value } = action.payload;
      if (state.volume.channels[channel]) {
        state.volume.channels[channel].temporaryValue = value;
      } else {
        throw new Error(`Volume channel ${channel} does not exist`);
      }
    },
    setUiVolumeChannel: (state, action: PayloadAction<string | null>) => {
      state.volume.uiChannel = action.payload;
    },
    setNoVolumeControl: (state, action: PayloadAction<boolean>) => {
      state.volume.noVolumeControl = action.payload;
    },
    /* Language */
    setLanguage: (
      state,
      action: PayloadAction<{
        locale: string;
        handle: string;
      }>
    ) => {
      state.language.current = action.payload.locale;
      state.language.handle = action.payload.handle;
      state.language.isRTL = getIsLanguageRTL(state.language.current);
    },
    setIsGameVisible: (state, action: PayloadAction<boolean>) => {
      state.isGameVisible = action.payload;
    },
    setIsStudioSoundDisabled: (state, action: PayloadAction<boolean>) => {
      state.isStudioSoundDisabled = action.payload;
    },
    // TODO: Revisit the code to handle game specific tabs from game level
    setSettingsTabToHide: (state, action: PayloadAction<string[]>) => {
      state.settingsTabToHide = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPlayerConfigAction.fulfilled, (state, action) => {
      const videoStream = action.payload.parsedPlayerConfig.videoStream;
      const compliance = action.payload.compliance;

      const videoVolume = parseParamNumber(
        action.payload.rawResponse.params.video_sounds,
        1,
        (value: number) => value / 100
      );
      const gameEffectsVolume = parseParamNumber(
        action.payload.rawResponse.params.game_effects,
        1,
        (value: number) => value / 100
      );
      const backgroundMusicVolume = parseParamNumber(
        action.payload.rawResponse.params.bg_music,
        1,
        (value: number) => value / 100
      );
      const voiceOverVolume = parseParamNumber(
        action.payload.rawResponse.params.vo_music,
        1,
        (value: number) => value / 100
      );
      const showOtherMessages = action.payload.rawResponse.params.showothermessages ?? true;

      const isSecondBetBoardEnabled = action.payload.rawResponse.params.issecondbetboardenabled ?? true;

      state.stream.isEnabled = videoStream;
      const { disableStudioSound } = compliance;

      // TODO: default channels should be mapped via game config
      if (state.volume.channels['studio'] && !disableStudioSound) {
        state.volume.channels['studio'].value = videoVolume;
        state.volume.channels['studio'].previousValue = videoVolume;
      }
      if (state.volume.channels['game']) {
        state.volume.channels['game'].value = gameEffectsVolume;
        state.volume.channels['game'].previousValue = gameEffectsVolume;
      }
      if (state.volume.channels['background'] && backgroundMusicVolume) {
        state.volume.channels['background'].value = backgroundMusicVolume;
        state.volume.channels['background'].previousValue = backgroundMusicVolume;
      }
      if (state.volume.channels['voiceover'] && voiceOverVolume) {
        state.volume.channels['voiceover'].value = voiceOverVolume;
        state.volume.channels['voiceover'].previousValue = voiceOverVolume;
      }
      state.showOtherMessages = showOtherMessages;
      // TODO: make this generic, for all games instead of HF specific, also add setting for this in core
      state.isAnimationEnabled = action.payload.rawResponse.params.hf_animation_enabled;
      state.isSecondBetBoardToggle = isSecondBetBoardEnabled;
      state.isStudioSoundDisabled = disableStudioSound ?? false;
    });
  },
});

// UTILS
const isAllChannelsHaveVolume = (channels: ChannelsType): boolean => {
  return Object.keys(channels).some((key) => (channels[key]?.value ?? 0) > 0);
};

const setMasterVolumeFunc = (state: ISettingsInitialState, newVolume: number): void => {
  state.volume.restored = true;
  const previousMasterValue = state.volume.master.value;
  state.volume.master.value = newVolume;
  
  // Behavior:
  // 1) When sliding master (non-zero → non-zero): sync Studio/Game (and others respecting conditions) to master
  // 2) When muting master (to 0): set Studio/Game to 0 but keep their previousValue for restoration
  // 3) When unmuting master (0 → >0): restore Studio/Game to their previousValue

  if (previousMasterValue === 0 && newVolume > 0) {
    // Master unmuted: restore channel previous values (minimum 1 if previous was 0)
    updateChannelsWithPreviousVolume(state, newVolume);
  } else {
    // Keep Studio and Game in sync with master when interacting with master slider
    updateChannelsWithMasterVolume(state, newVolume, newVolume !== 0);
  }

  updateMasterVolume(state, newVolume);
};

const shouldSkipChannel = (state: ISettingsInitialState, key: string): boolean => {
  if (key === VolumeChannels.Studio) {
    if (!state.stream.isEnabled) return true;
    if (state.isStudioSoundDisabled) return true;
  }
  return false;
}

// Syncing other channels(game/studio) volumes to server
const updateChannelsWithMasterVolume = (state: ISettingsInitialState, newVolume: number, updatePreviousValue: boolean = true): void => {
  Object.keys(state.volume.channels).forEach((key) => {
    const channel = state.volume.channels[key];
    if (!channel) throw new Error(`Volume channel ${key} does not exist`);
    if (shouldSkipChannel(state, key)) return;

    if (updatePreviousValue) {
      channel.previousValue = newVolume;
    }
    channel.value = newVolume;
    channel.temporaryValue = null;
  });
}

// Restoring all channels' volume to previous value when master is unmuted
const updateChannelsWithPreviousVolume = (state: ISettingsInitialState, newVolume: number): void => {
  Object.keys(state.volume.channels).forEach((key) => {
    if (state.volume.noVolumeControl && !state.stream.isEnabled && key === 'studio') return;

    // For Brazilian jurisdiction -> disable studio sound if dedicated language is not 'pt'
    if (state.isStudioSoundDisabled && key === 'studio') return;

    if (!state.volume.channels[key]) throw new Error(`Volume channel ${key} does not exist`);

    const newValues = state.volume.channels[key].previousValue > 0 ? state.volume.channels[key].previousValue : newVolume;
    state.volume.channels[key].value = newValues;
    state.volume.channels[key].previousValue = newValues;
  });
};

const updateMasterVolume = (state: ISettingsInitialState, newVolume: number): void => {
  if (newVolume) {
    state.volume.master.previousValue = newVolume;
    CoreSessionStorage.setLastPositiveMasterVolume(newVolume);
  }

  state.volume.master.temporaryValue = null;
  CoreSessionStorage.setLastMasterVolume(newVolume);
};

const setVolumeValue = (
  state: ISettingsInitialState,
  action: PayloadAction<{ channel: keyof (typeof initialState)['volume']['channels']; value: number }>
): void => {
  const { channel, value } = action.payload;
  state.volume.restored = true;

  if (state.volume.channels[channel]) {
    updateChannelVolume(state, channel, value);
    checkAndUpdateMasterVolume(state, value);
  } else {
    throw new Error(`Volume channel ${channel} does not exist`);
  }
};

const updateChannelVolume = (
  state: ISettingsInitialState,
  channel: keyof (typeof initialState)['volume']['channels'],
  value: number
): void => {
  const channelState = state.volume.channels[channel];
  if (!channelState) return;
  channelState.value = value;
  if (value) {
    channelState.previousValue = value;
  }
  channelState.temporaryValue = null;
};

const checkAndUpdateMasterVolume = (state: ISettingsInitialState, value: number): void => {
  if (value === 0 && !isAllChannelsHaveVolume(state.volume.channels)) {
    state.volume.master.value = 0;
  }
};

export const settingsActions = settingsSlice.actions;
